import { useEffect, useRef, useState, type JSX } from 'react';
import { AuthUser } from '../types';
import { AuthContext } from './AuthContext';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FirebaseApp } from 'firebase/app';

// Provider hook that handles state and create the auth object
export const AuthProvider = ({ firebaseApp, children }: { firebaseApp: FirebaseApp; children: JSX.Element }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  // Ignore a stale auth callback: the claims fetch below is async, so a
  // callback for a previous account can resolve after a newer one and set
  // `user` back to the old account. Each callback captures a generation and
  // only commits if it is still the latest.
  const generation = useRef(0);

  useEffect(() => {
    // We track the auth state to reset firebaseUi if the user signs out.
    const unregisterAuthObserver = onAuthStateChanged(getAuth(firebaseApp), async (user) => {
      const gen = ++generation.current;
      const newUser: AuthUser | null = user;
      if (newUser) {
        // Get claims from the token and add it to the user object.
        const idTokenResult = await getAuth(firebaseApp).currentUser?.getIdTokenResult(true);
        newUser.claims = idTokenResult?.claims ?? {};
      }

      // A newer auth-state change superseded this one while its claims were
      // being fetched; drop it so it cannot revert to an older account.
      if (gen !== generation.current) {
        return;
      }

      setUser(newUser);
      setIsInitializing(false);
    });

    return () => {
      unregisterAuthObserver();
    };
  }, [firebaseApp]);

  const values = {
    user,
    setUser,
    isInitializing,
    setIsInitializing,
    firebaseApp,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};
